<template>
	<div class="container">
		<h1>Admin: Tools</h1>

		<div class="card shadow-sm tool-card">
			<div class="card-body">
				<div class="d-flex flex-column flex-md-row gap-3 align-items-md-center">
					<div class="flex-grow-1">
						<h2 class="h5 mb-1">Daily Admin Digest</h2>
						<p class="text-muted mb-0">
							Send the platform digest for the last 24 hours.
						</p>
					</div>
					<button
						type="button"
						class="btn btn-outline-secondary"
						:disabled="previewLoading"
						@click="loadDigestPreview">
						<span
							v-if="previewLoading"
							class="spinner-border spinner-border-sm me-2"
							aria-hidden="true"></span>
						{{ previewLoading ? "Loading..." : "Preview" }}
					</button>
					<button
						type="button"
						class="btn btn-primary"
						:disabled="digestLoading"
						@click="runAdminDigest">
						<span
							v-if="digestLoading"
							class="spinner-border spinner-border-sm me-2"
							aria-hidden="true"></span>
						{{ digestLoading ? "Sending..." : "Send Digest" }}
					</button>
				</div>

				<div
					v-if="digestMessage"
					class="alert mt-3 mb-0"
					:class="digestError ? 'alert-danger' : 'alert-success'">
					{{ digestMessage }}
				</div>

				<div v-if="previewHtml" class="mt-4">
					<div class="d-flex flex-wrap gap-2 justify-content-between mb-2">
						<div>
							<strong>{{ previewSubject }}</strong>
							<div class="text-muted small">
								To: {{ previewRecipient }}
							</div>
						</div>
					</div>
					<iframe class="digest-preview" :srcdoc="previewHtml"></iframe>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { ref } from "vue"
	import { fetchWithAuth } from "@/utils/api-client"

	const digestLoading = ref(false)
	const digestMessage = ref("")
	const digestError = ref(false)
	const previewLoading = ref(false)
	const previewHtml = ref("")
	const previewSubject = ref("")
	const previewRecipient = ref("")

	async function loadDigestPreview() {
		previewLoading.value = true
		digestMessage.value = ""
		digestError.value = false

		try {
			const response = await fetchWithAuth("/api/admin/digest")
			const data = await response.json().catch(() => ({}))

			if (!response.ok) {
				throw new Error(data?.message || data?.error || "Failed to load preview.")
			}

			previewHtml.value = data.html || ""
			previewSubject.value = data.subject || "Daily Admin Digest"
			previewRecipient.value = data.recipient || ""
		} catch (error) {
			digestError.value = true
			digestMessage.value = error.message || "Failed to load preview."
		} finally {
			previewLoading.value = false
		}
	}

	async function runAdminDigest() {
		digestLoading.value = true
		digestMessage.value = ""
		digestError.value = false

		try {
			const response = await fetchWithAuth("/api/admin/digest", {
				method: "POST",
			})
			const data = await response.json().catch(() => ({}))

			if (!response.ok) {
				throw new Error(data?.message || data?.error || "Failed to send digest.")
			}

			digestMessage.value = `Digest sent for ${data.windowLabel || data.reportDate}. New users: ${data.newUsers}. Active users: ${data.activeUsers}.`
		} catch (error) {
			digestError.value = true
			digestMessage.value = error.message || "Failed to send digest."
		} finally {
			digestLoading.value = false
		}
	}
</script>

<style scoped>
	.tool-card {
		max-width: 760px;
	}

	.digest-preview {
		width: 100%;
		min-height: 520px;
		border: 1px solid #dee2e6;
		border-radius: 6px;
		background: #fff;
	}
</style>
